import { useState } from 'react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext.jsx';
import { db } from '../lib/firebase.js';
import { BRANCHES, STATUSES, combinedLabel } from '../lib/branches.js';
import BranchBadge, { StatusSelect } from '../components/BranchBadge.jsx';
import SocHeader from '../components/SocHeader.jsx';
import Footer from '../components/Footer.jsx';

export default function Profile() {
  const { user, profile, logout, refreshProfile } = useAuth();
  const [editingBranch, setEditingBranch] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const currentBranch = profile?.branch || '';
  const currentStatus = profile?.status || '';

  const startEdit = () => {
    setSelectedStatus(currentStatus);
    setSelectedBranch(currentBranch);
    setSaveMsg('');
    setEditingBranch(true);
  };

  const cancelEdit = () => {
    setEditingBranch(false);
    setSaveMsg('');
  };

  const saveService = async () => {
    if (!user) return;
    setSaving(true);
    setSaveMsg('');
    try {
      await setDoc(
        doc(db, 'users', user.uid),
        {
          status: selectedStatus || null,
          branch: selectedBranch || null,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      refreshProfile();
      setEditingBranch(false);
      setSaveMsg('Service info updated.');
    } catch (e) {
      setSaveMsg('Save failed — ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const hasService = currentBranch || currentStatus;
  const noChange = selectedStatus === currentStatus && selectedBranch === currentBranch;

  return (
    <>
      <SocHeader />
      <section className="auth-section" id="main">
        <div className="auth-card auth-card-wide">
          <div className="hero-eyebrow">OPERATOR DOSSIER</div>
          <h2 className="auth-headline">Profile</h2>

          {/* Branch / Status Banner */}
          {hasService && !editingBranch && (
            <div className="service-card">
              {currentBranch && <BranchBadge branchKey={currentBranch} statusKey={currentStatus} size={72} />}
              <div className="service-card-info">
                <div className="service-card-label">BRANCH OF SERVICE</div>
                <div className="service-card-name">{combinedLabel(currentStatus, currentBranch)}</div>
                <button type="button" className="branch-edit-btn" onClick={startEdit}>
                  Edit Service Info
                </button>
              </div>
            </div>
          )}

          {/* Service Info Editor (edit mode or nothing set yet) */}
          {(editingBranch || !hasService) && (
            <div className="branch-editor">
              {/* Step 1 — Status */}
              <div className="form-group">
                <label className="form-label" htmlFor="status-edit">
                  Step 1 — Status
                </label>
                <StatusSelect
                  id="status-edit"
                  value={selectedStatus}
                  onChange={setSelectedStatus}
                  disabled={saving}
                />
              </div>

              {/* Step 2 — Branch */}
              <div className="form-group" style={{ marginTop: '0.75rem' }}>
                <label className="form-label" htmlFor="branch-edit">
                  Step 2 — Branch of Service
                </label>
                <div className="branch-select-row">
                  {selectedBranch && (
                    <BranchBadge branchKey={selectedBranch} statusKey={selectedStatus} size={40} />
                  )}
                  <select
                    id="branch-edit"
                    className="form-input branch-select"
                    value={selectedBranch}
                    onChange={(e) => setSelectedBranch(e.target.value)}
                    disabled={saving}
                  >
                    <option value="">-- Select Branch --</option>
                    {BRANCHES.map((b) => (
                      <option key={b.key} value={b.key}>{b.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Combined preview */}
              {(selectedStatus || selectedBranch) && (
                <div className="branch-preview-label">
                  ↳ {combinedLabel(selectedStatus, selectedBranch) || '—'}
                </div>
              )}

              <div className="branch-editor-actions">
                <button
                  type="button"
                  className="btn"
                  onClick={saveService}
                  disabled={saving || noChange}
                >
                  {saving ? 'SAVING…' : 'Save →'}
                </button>
                {editingBranch && (
                  <button type="button" className="btn btn-ghost" onClick={cancelEdit} disabled={saving}>
                    Cancel
                  </button>
                )}
              </div>

              {saveMsg && (
                <div className={`form-msg ${saveMsg.includes('failed') ? 'error' : 'success'}`}>
                  {saveMsg}
                </div>
              )}
            </div>
          )}

          {saveMsg && !editingBranch && hasService && (
            <div className="form-msg success">{saveMsg}</div>
          )}

          <div className="profile-grid" style={{ marginTop: '1.5rem' }}>
            <div className="profile-row"><span className="form-label">Display Name</span><span>{user?.displayName || '—'}</span></div>
            <div className="profile-row"><span className="form-label">Email</span><span>{user?.email}</span></div>
            <div className="profile-row"><span className="form-label">UID</span><span style={{ fontSize: 11 }}>{user?.uid}</span></div>
            <div className="profile-row"><span className="form-label">Provider</span><span>{user?.providerData?.[0]?.providerId || '—'}</span></div>
            <div className="profile-row"><span className="form-label">Email Verified</span><span>{user?.emailVerified ? 'YES' : 'NO'}</span></div>
            {profile?.createdAt && (
              <div className="profile-row"><span className="form-label">Enlisted</span><span>{profile.createdAt.toDate?.().toLocaleString() || '—'}</span></div>
            )}
          </div>

          <div className="callout" style={{ marginTop: '2rem' }}>
            <strong>Sync Coming Online</strong>
            Cross-device flashcard progress, exam history, and saved notes will sync to your account
            in upcoming releases. Stand by.
          </div>

          <button type="button" className="btn btn-ghost btn-block" onClick={logout} style={{ marginTop: '1.5rem' }}>
            Sign Out →
          </button>
        </div>
      </section>
      <Footer />
    </>
  );
}
